

import React, { useRef, useState } from 'react';
import {
    Button,
    Modal,
    Upload,
    Table,
    Alert,
    Space,
    Typography,
    Tag,
    Tooltip,
    Select,
    Row,
    Col,
    Divider,
    Badge,
    Progress,
    Segmented,
    List,
    Popconfirm,
    Input,
    theme
} from 'antd';
import {
    UploadOutlined,
    CheckCircleOutlined,
    FileTextOutlined,
    ClockCircleOutlined,
    TranslationOutlined,
    TeamOutlined,
    ThunderboltOutlined,
    UserOutlined,
    SaveOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import { TriggerType, PushTemplate } from '../../types/push_management.types';
import { Segment } from '../../types/push.types';
import { translateBatch, SUPPORTED_LANGUAGES } from '../../utils/translation';
import { useAppDispatch, useAppSelector } from 'store';
import {
    createTemplate,
    fetchTriggerPresets,
    createTriggerPreset,
    deleteTriggerPreset,
    selectTriggerPresets,
    selectPushLoading,
} from 'store/slices/push.slice';

const { Text, Title } = Typography;

const LANG_OPTIONS = SUPPORTED_LANGUAGES.map((l) => ({
    value: l.code,
    label: `${l.abbr} — ${l.name}`,
}));

const TRIGGER_TYPE_CONFIG: Record<TriggerType, { color: string; label: string; icon: React.ReactNode }> = {
    install:       { color: 'blue',   label: 'Установка',       icon: <UserOutlined /> },
    registration:  { color: 'green',  label: 'Регистрация',     icon: <UserOutlined /> },
    deposit:       { color: 'gold',   label: 'Депозит',         icon: <UserOutlined /> },
    first_deposit: { color: 'orange', label: 'Первый депозит',  icon: <UserOutlined /> },
};

export interface InlinePushContent {
    title: string;
    body: string;
    image_url?: string;
}

export interface TriggerItem {
    name: string;
    trigger_type: TriggerType;
    delay_minutes?: number;
    max_sends_per_user?: number;
    cooldown_hours?: number;
    is_active?: boolean;
    segment_id?: number;
    campaign_id?: number;
    push_template_id?: number;
    push?: InlinePushContent;
}

export interface TriggerTemplate {
    name?: string;
    lang?: string;
    default_segment_id?: number;
    default_campaign_id?: number;
    triggers: TriggerItem[];
}

export interface LoadedTrigger {
    name: string;
    trigger_type: TriggerType;
    delay_minutes: number;
    max_sends_per_user: number;
    cooldown_hours: number;
    is_active: boolean;
    push_template_id: number;
    segment_id?: number;
    campaign_id?: number;
}

interface EventTriggerLoaderProps {
    disabled?: boolean;
    appId?: number;
    segments?: Segment[];
    templates?: PushTemplate[];
    onLoad: (triggers: LoadedTrigger[], templateName?: string, targetLang?: string) => void;
}

function parseTriggerTemplate(raw: unknown): TriggerTemplate {
    if (typeof raw !== 'object' || raw === null || !('triggers' in raw)) {
        throw new Error('Неверный формат: ожидается объект с полем "triggers"');
    }
    const tpl = raw as Record<string, unknown>;
    if (!Array.isArray(tpl.triggers) || tpl.triggers.length === 0) {
        throw new Error('Поле "triggers" должно быть непустым массивом');
    }
    tpl.triggers.forEach((s: unknown, i: number) => {
        if (typeof s !== 'object' || s === null) {
            throw new Error(`triggers[${i}]: ожидается объект`);
        }
        const item = s as Record<string, unknown>;
        if (!item.name) throw new Error(`triggers[${i}]: отсутствует поле "name"`);
        if (!item.trigger_type) throw new Error(`triggers[${i}]: отсутствует поле "trigger_type"`);
        if (!['install', 'registration', 'deposit', 'first_deposit'].includes(item.trigger_type as string)) {
            throw new Error(`triggers[${i}]: недопустимый trigger_type "${item.trigger_type}"`);
        }
        if (!item.push_template_id && !item.push) {
            throw new Error(
                `triggers[${i}]: нужен "push_template_id" или "push" с контентом (title + body)`
            );
        }
        if (item.push) {
            const push = item.push as Record<string, unknown>;
            if (!push.title || !push.body) {
                throw new Error(`triggers[${i}].push: обязательны поля "title" и "body"`);
            }
        }
    });
    return raw as TriggerTemplate;
}

function norm(s: string): string {
    return s.toLowerCase().trim().replace(/\s+/g, ' ');
}

function findTemplateByContent(
    templates: PushTemplate[],
    title: string,
    body: string,
): PushTemplate | undefined {
    const nTitle = norm(title);
    const nBody  = norm(body);
    return templates.find(t => norm(t.title) === nTitle && norm(t.body) === nBody);
}

function formatDelay(minutes: number): string {
    if (minutes === 0) return 'Сразу';
    if (minutes < 60) return `${minutes} мин`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours} ч`;
    return `${hours} ч ${mins} мин`;
}

type Step = 'upload' | 'edit' | 'processing' | 'preview';
type UploadMode = 'file' | 'preset';

const EventTriggerLoader: React.FC<EventTriggerLoaderProps> = ({
    disabled = false,
    appId,
    segments = [],
    templates: existingTemplates = [],
    onLoad,
}) => {
    const { token } = theme.useToken();
    const dispatch = useAppDispatch();
    const presets = useAppSelector(selectTriggerPresets);
    const presetsLoading = useAppSelector(selectPushLoading);
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState<Step>('upload');
    const [uploadMode, setUploadMode] = useState<UploadMode>('file');
    const [template, setTemplate] = useState<TriggerTemplate | null>(null);
    const [globalSegmentId, setGlobalSegmentId] = useState<number | undefined>(undefined);
    const [overrides, setOverrides] = useState<Record<number, Partial<TriggerItem>>>({});
    const [targetLang, setTargetLang] = useState<string | undefined>(undefined);
    const [translatedOverrides, setTranslatedOverrides] = useState<
        Record<number, { name: string; push?: InlinePushContent }>
    >({});
    const [resolvedTemplateIds, setResolvedTemplateIds] = useState<Record<number, number>>({});
    const [progress, setProgress] = useState(0);
    const [progressLabel, setProgressLabel] = useState('');
    const [processingLog, setProcessingLog] = useState<string[]>([]);
    const stopRef = useRef(false);
    const [error, setError] = useState<string | null>(null);
    const [savePresetOpen, setSavePresetOpen] = useState(false);
    const [savePresetName, setSavePresetName] = useState('');
    const [savePresetDesc, setSavePresetDesc] = useState('');
    const [savingPreset, setSavingPreset] = useState(false);
    const inlinePushCount = template?.triggers.filter(s => s.push && !s.push_template_id).length ?? 0;
    const reset = () => {
        setStep('upload');
        setUploadMode('file');
        setTemplate(null);
        setGlobalSegmentId(undefined);
        setOverrides({});
        setTargetLang(undefined);
        setTranslatedOverrides({});
        setResolvedTemplateIds({});
        setProgress(0);
        setProgressLabel('');
        setProcessingLog([]);
        setError(null);
        stopRef.current = false;
    };

    const handleOpen = () => {
        reset();
        setOpen(true);
        dispatch(fetchTriggerPresets());
    };

    const handleClose = () => { stopRef.current = true; setOpen(false); };

    const addProcessingLog = (msg: string) => {
        setProcessingLog(prev => [...prev, msg]);
    };

    const handleLoadPreset = (presetJsonData: unknown) => {
        setError(null);
        try {
            const tpl = parseTriggerTemplate(presetJsonData);
            setTemplate(tpl);
            if (tpl.default_segment_id) setGlobalSegmentId(tpl.default_segment_id);
            setStep('edit');
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Невалидный пресет');
        }
    };

    const handleDeletePreset = async (id: number) => {
        try {
            await dispatch(deleteTriggerPreset(id)).unwrap();
        } catch (err: any) {
            setError(err?.message ?? 'Не удалось удалить пресет');
        }
    };

    const handleSavePreset = async () => {
        if (!template || !savePresetName.trim()) return;
        setSavingPreset(true);
        try {
            await dispatch(createTriggerPreset({
                name: savePresetName.trim(),
                description: savePresetDesc.trim() || undefined,
                json_data: template,
            })).unwrap();
            setSavePresetOpen(false);
            setSavePresetName('');
            setSavePresetDesc('');
        } catch (err: any) {
            setError(err?.message ?? 'Не удалось сохранить пресет');
        } finally {
            setSavingPreset(false);
        }
    };

    const openSavePresetModal = () => {
        setSavePresetName(template?.name ?? '');
        setSavePresetDesc('');
        setSavePresetOpen(true);
    };

    const getMergedItem = (item: TriggerItem, index: number): TriggerItem => ({
        ...item,
        ...overrides[index],
        ...(translatedOverrides[index]
            ? {
                name: translatedOverrides[index].name,
                push: translatedOverrides[index].push ?? item.push,
            }
            : {}
        ),
        delay_minutes: overrides[index]?.delay_minutes ?? item.delay_minutes ?? 0,
        max_sends_per_user: overrides[index]?.max_sends_per_user ?? item.max_sends_per_user ?? 1,
        cooldown_hours: overrides[index]?.cooldown_hours ?? item.cooldown_hours ?? 24,
        is_active: overrides[index]?.is_active !== undefined
            ? overrides[index].is_active
            : item.is_active !== undefined ? item.is_active : true,
        segment_id: overrides[index]?.segment_id !== undefined
            ? overrides[index].segment_id
            : item.segment_id !== undefined ? item.segment_id : globalSegmentId,
    });

    const handleFileRead = (file: UploadFile): boolean => {
        setError(null);
        const raw = file.originFileObj ?? (file as unknown as File);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const parsed = JSON.parse(e.target?.result as string);
                const tpl = parseTriggerTemplate(parsed);
                setTemplate(tpl);
                if (tpl.default_segment_id) setGlobalSegmentId(tpl.default_segment_id);
                setStep('edit');
            } catch (err: unknown) {
                setError(err instanceof Error ? err.message : 'Ошибка парсинга JSON');
            }
        };
        reader.readAsText(raw);
        return false;
    };

    const handleProcess = async () => {
        if (!template) return;

        const srcLang = template.lang ?? 'en';
        const needTranslate = targetLang && targetLang !== srcLang;
        const hasInlinePush = template.triggers.some(s => s.push && !s.push_template_id);
        const needProcessing = needTranslate || hasInlinePush;

        if (!needProcessing) {
            setTranslatedOverrides({});
            setResolvedTemplateIds({});
            setStep('preview');
            return;
        }

        stopRef.current = false;
        setStep('processing');
        setProgress(0);
        setProcessingLog([]);

        const templateCache = [...existingTemplates];
        const newResolvedIds: Record<number, number> = {};
        const newTranslated: Record<number, { name: string; push?: InlinePushContent }> = {};

        if (needTranslate) {
            addProcessingLog('🌍 Перевод...');

            const items: { key: string; text: string }[] = [];
            template.triggers.forEach((s, i) => {
                if (s.push) {
                    items.push({ key: `${i}_push_title`, text: s.push.title });
                    items.push({ key: `${i}_push_body`, text: s.push.body });
                }
            });

            try {
                const translations = await translateBatch(
                    items,
                    {
                        from: srcLang,
                        to: targetLang!,
                        delay: 150,
                        onProgress: (done, total, key) => {
                            const pct = Math.round((done / total) * (hasInlinePush ? 50 : 100));
                            setProgress(pct);
                            if (key.includes('push_title')) setProgressLabel('Push заголовок...');
                            else if (key.includes('push_body')) setProgressLabel('Push текст...');
                            else setProgressLabel('Название...');
                        },
                    },
                    stopRef,
                );

                if (stopRef.current) return;

                template.triggers.forEach((s, i) => {
                    newTranslated[i] = {
                        name: s.name,
                        ...(s.push ? {
                            push: {
                                title: translations[`${i}_push_title`] ?? s.push.title,
                                body: translations[`${i}_push_body`] ?? s.push.body,
                                image_url: s.push.image_url,
                            },
                        } : {}),
                    };
                });

                addProcessingLog(`✅ Переведено ${items.length} текстов`);
            } catch (err: unknown) {
                setError(err instanceof Error ? err.message : 'Ошибка перевода');
                setStep('edit');
                return;
            }
        }

        if (hasInlinePush && appId) {
            addProcessingLog('🔍 Поиск / создание шаблонов...');

            const inlineItems = template.triggers
                .map((s, i) => ({ s, i }))
                .filter(({ s }) => s.push && !s.push_template_id);

            for (let idx = 0; idx < inlineItems.length; idx++) {
                if (stopRef.current) {
                    addProcessingLog('⏹ Остановлено');
                    break;
                }

                const { s, i } = inlineItems[idx];
                const pushContent = newTranslated[i]?.push ?? s.push!;
                const title = pushContent.title;
                const body = pushContent.body;

                const basePct = needTranslate ? 50 : 0;
                const pct = basePct + Math.round(((idx + 1) / inlineItems.length) * (100 - basePct));
                setProgress(pct);
                setProgressLabel(`Шаблон ${idx + 1}/${inlineItems.length}: ${s.name}`);

                const existing = findTemplateByContent(templateCache, title, body);

                if (existing) {
                    newResolvedIds[i] = existing.id;
                    addProcessingLog(`⚡ "${s.name}" → существующий шаблон "${existing.name}" (id:${existing.id})`);
                } else {
                    const templateName = `${newTranslated[i]?.name ?? s.name} [auto]`;
                    try {
                        const result = await dispatch(createTemplate({
                            app_id: appId,
                            name: templateName,
                            description: `Автосоздан при импорте триггеров`,
                            title,
                            body,
                            image_url: pushContent.image_url,
                        })).unwrap();

                        const newId = result.template?.id;
                        if (newId) {
                            newResolvedIds[i] = newId;
                            templateCache.push(result.template);
                            addProcessingLog(`✅ "${s.name}" → создан шаблон "${templateName}" (id:${newId})`);
                        }
                    } catch (err: any) {
                        const msg = (err?.message || '').toLowerCase();
                        if (msg.includes('409') || msg.includes('already exist') || msg.includes('duplicate')) {
                            const retry = findTemplateByContent(templateCache, title, body);
                            if (retry) {
                                newResolvedIds[i] = retry.id;
                                addProcessingLog(`⚡ "${s.name}" → конфликт, используем (id:${retry.id})`);
                            } else {
                                addProcessingLog(`❌ "${s.name}" → не удалось создать шаблон: конфликт`);
                            }
                        } else {
                            addProcessingLog(`❌ "${s.name}" → ошибка: ${(err?.message || '').slice(0, 100)}`);
                        }
                    }
                }
            }
        }

        if (stopRef.current) return;

        setTranslatedOverrides(newTranslated);
        setResolvedTemplateIds(newResolvedIds);
        setStep('preview');
    };

    const handleApply = () => {
        if (!template) return;

        const unresolved: number[] = [];
        const result: LoadedTrigger[] = template.triggers.map((item, i) => {
            const merged = getMergedItem(item, i);
            const templateId = merged.push_template_id ?? resolvedTemplateIds[i];

            if (!templateId) {
                unresolved.push(i);
            }

            return {
                name: merged.name,
                trigger_type: merged.trigger_type,
                delay_minutes: merged.delay_minutes ?? 0,
                max_sends_per_user: merged.max_sends_per_user ?? 1,
                cooldown_hours: merged.cooldown_hours ?? 24,
                is_active: merged.is_active ?? true,
                push_template_id: templateId ?? 0,
                segment_id: merged.segment_id,
                campaign_id: merged.campaign_id,
            };
        });

        if (unresolved.length > 0) {
            setError(`Не удалось определить шаблон для триггеров: ${unresolved.map(i => `#${i + 1}`).join(', ')}`);
            return;
        }

        onLoad(result, template.name, targetLang);
        setOpen(false);
    };

    const setOverride = (index: number, patch: Partial<TriggerItem>) => {
        setOverrides(prev => ({ ...prev, [index]: { ...prev[index], ...patch } }));
    };

    const getSegmentName = (segmentId?: number): string => {
        if (!segmentId) return 'Все пользователи';
        return segments.find(s => s.id === segmentId)?.name ?? `#${segmentId}`;
    };

    const getTemplateBadge = (item: TriggerItem, index: number) => {
        if (item.push_template_id) {
            return <Tag color="green" style={{ fontSize: 11 }}>ID:{item.push_template_id}</Tag>;
        }
        if (resolvedTemplateIds[index]) {
            return <Tag color="cyan" style={{ fontSize: 11 }}>→ ID:{resolvedTemplateIds[index]}</Tag>;
        }
        if (item.push) {
            return (
                <Tooltip title={`${item.push.title}\n${item.push.body}`}>
                    <Tag color="orange" icon={<ThunderboltOutlined />} style={{ fontSize: 11 }}>
                        Авто
                    </Tag>
                </Tooltip>
            );
        }
        return <Tag color="red" style={{ fontSize: 11 }}>Нет шаблона</Tag>;
    };

    const previewColumns: ColumnsType<TriggerItem> = [
        {
            title: '№',
            width: 44,
            align: 'center',
            render: (_, __, i) => <Text type="secondary" style={{ fontSize: 12 }}>{i + 1}</Text>,
        },
        {
            title: 'Название',
            render: (_, record, i) => (
                <div>
                    <Space size={6}>
                        <Text strong style={{ fontSize: 13 }}>{record.name}</Text>
                        {getTemplateBadge(record, i)}
                    </Space>
                    {record.push && (
                        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                            📝 {record.push.title} — {record.push.body.slice(0, 60)}{record.push.body.length > 60 ? '…' : ''}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Событие',
            width: 150,
            dataIndex: 'trigger_type',
            render: (type: TriggerType) => {
                const cfg = TRIGGER_TYPE_CONFIG[type];
                return <Tag color={cfg.color} icon={cfg.icon}>{cfg.label}</Tag>;
            },
        },
        {
            title: 'Задержка',
            width: 120,
            render: (_, record, i) => {
                const delay = overrides[i]?.delay_minutes ?? record.delay_minutes ?? 0;
                return (
                    <Space>
                        <ClockCircleOutlined style={{ color: '#999' }} />
                        <Text style={{ fontSize: 13 }}>{formatDelay(delay)}</Text>
                    </Space>
                );
            },
        },
        {
            title: 'Лимиты',
            width: 140,
            render: (_, record, i) => {
                const max = overrides[i]?.max_sends_per_user ?? record.max_sends_per_user ?? 1;
                const cd = overrides[i]?.cooldown_hours ?? record.cooldown_hours ?? 24;
                return (
                    <Space direction="vertical" size={0}>
                        <Text style={{ fontSize: 12 }}>Макс: {max} раз</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>Cooldown: {cd}ч</Text>
                    </Space>
                );
            },
        },
        {
            title: 'Сегмент',
            width: 170,
            render: (_, record, i) => {
                const segId = overrides[i]?.segment_id !== undefined
                    ? overrides[i].segment_id
                    : record.segment_id !== undefined ? record.segment_id : globalSegmentId;
                return (
                    <Select
                        size="small" value={segId ?? undefined} placeholder="Все"
                        style={{ width: 150 }} allowClear
                        onChange={(val) => setOverride(i, { segment_id: val ?? undefined })}
                        showSearch optionFilterProp="label"
                        options={segments.map(s => ({ value: s.id, label: s.name }))}
                    />
                );
            },
        },
        {
            title: '',
            width: 60,
            align: 'center',
            render: (_, record, i) => {
                const active = overrides[i]?.is_active !== undefined
                    ? overrides[i].is_active
                    : record.is_active !== undefined ? record.is_active : true;
                return (
                    <Badge
                        status={active ? 'success' : 'default'}
                        text={
                            <Text style={{ fontSize: 12, cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => setOverride(i, { is_active: !active })}
                            >
                                {active ? 'Да' : 'Нет'}
                            </Text>
                        }
                    />
                );
            },
        },
    ];

    const footer = () => {
        if (step === 'upload') return <Button onClick={handleClose}>Отмена</Button>;
        if (step === 'edit') {
            return (
                <Space>
                    <Button onClick={reset}>← Назад</Button>
                    <Button type="primary" onClick={handleProcess}>
                        {(targetLang || inlinePushCount > 0) ? 'Обработать и предпросмотр →' : 'Предпросмотр →'}
                    </Button>
                </Space>
            );
        }
        if (step === 'processing') return <Button danger onClick={handleClose}>Отменить</Button>;
        return (
            <Space>
                <Button onClick={() => { setTranslatedOverrides({}); setResolvedTemplateIds({}); setStep('edit'); }}>
                    ← Изменить
                </Button>
                <Button type="primary" icon={<CheckCircleOutlined />} onClick={handleApply}>
                    Применить {template?.triggers.length} триггеров
                </Button>
            </Space>
        );
    };

    const content = () => {
        if (step === 'upload') {
            return (
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    {error && <Alert type="error" message={error} showIcon />}

                    <Segmented
                        block
                        value={uploadMode}
                        onChange={(v) => setUploadMode(v as UploadMode)}
                        options={[
                            { label: 'Из файла', value: 'file', icon: <UploadOutlined /> },
                            {
                                label: `Пресеты${presets.length ? ` (${presets.length})` : ''}`,
                                value: 'preset',
                                icon: <FileTextOutlined />,
                            },
                        ]}
                    />

                    {uploadMode === 'file' && (
                        <div>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                                JSON с триггерами. Push-контент определяется двумя способами:
                                {' '}<Text code>push_template_id</Text> → готовый шаблон,
                                {' '}или <Text code>push</Text> → объект с title/body (шаблон создастся автоматически).
                            </Text>
                            <pre style={{
                                background: token.colorFillQuaternary, border: `1px solid ${token.colorBorderSecondary}`, padding: '10px 14px',
                                fontSize: 11, color: token.colorTextSecondary, marginBottom: 16, overflow: 'auto',
                                maxHeight: 220,
                            }}>
                                {`{
  "name": "Onboarding Triggers Pack",
  "lang": "en",
  "triggers": [
    {
      "name": "Welcome after Registration",
      "trigger_type": "registration",
      "delay_minutes": 60,
      "push": {
        "title": "Welcome! 🎉",
        "body": "Thanks for joining! Start playing now."
      }
    },
    {
      "name": "First Deposit Congrats",
      "trigger_type": "first_deposit",
      "delay_minutes": 0,
      "max_sends_per_user": 1,
      "push": {
        "title": "Congratulations! 💰",
        "body": "Your first deposit bonus is ready!"
      }
    },
    {
      "name": "Re-engage after Install",
      "trigger_type": "install",
      "delay_minutes": 1440,
      "push_template_id": 42
    }
  ]
}`}
                            </pre>
                            <Upload accept=".json" showUploadList={false} beforeUpload={handleFileRead}>
                                <Button icon={<UploadOutlined />} size="large" block>Выбрать JSON-файл</Button>
                            </Upload>
                        </div>
                    )}

                    {uploadMode === 'preset' && (
                        <div>
                            {presets.length === 0 && !presetsLoading ? (
                                <Alert
                                    type="info"
                                    showIcon
                                    message="Пресетов ещё нет"
                                    description="Загрузите JSON-файл и сохраните его как пресет — он будет доступен всем пользователям."
                                />
                            ) : (
                                <List
                                    loading={presetsLoading}
                                    dataSource={presets}
                                    size="small"
                                    style={{ maxHeight: 360, overflow: 'auto' }}
                                    renderItem={(p) => {
                                        const data = p.json_data as TriggerTemplate | undefined;
                                        const triggersCount = Array.isArray(data?.triggers) ? data!.triggers.length : 0;
                                        return (
                                            <List.Item
                                                actions={[
                                                    <Button
                                                        key="load"
                                                        type="primary"
                                                        size="small"
                                                        onClick={() => handleLoadPreset(p.json_data)}
                                                    >
                                                        Загрузить
                                                    </Button>,
                                                    <Popconfirm
                                                        key="del"
                                                        title="Удалить пресет?"
                                                        description="Действие необратимо."
                                                        okText="Удалить"
                                                        okType="danger"
                                                        cancelText="Отмена"
                                                        onConfirm={() => handleDeletePreset(p.id)}
                                                    >
                                                        <Button size="small" danger icon={<DeleteOutlined />} />
                                                    </Popconfirm>,
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    title={<Text strong>{p.name}</Text>}
                                                    description={
                                                        <Space direction="vertical" size={2}>
                                                            {p.description && (
                                                                <Text type="secondary" style={{ fontSize: 12 }}>{p.description}</Text>
                                                            )}
                                                            <Space size={6} wrap>
                                                                <Tag color="blue" style={{ fontSize: 11 }}>
                                                                    {triggersCount} триггеров
                                                                </Tag>
                                                                {data?.lang && (
                                                                    <Tag style={{ fontSize: 11 }}>lang: {data.lang}</Tag>
                                                                )}
                                                            </Space>
                                                        </Space>
                                                    }
                                                />
                                            </List.Item>
                                        );
                                    }}
                                />
                            )}
                        </div>
                    )}

                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            <TranslationOutlined /> Язык перевода (необязательно)
                        </Text>
                        <Select
                            style={{ width: '100%' }}
                            placeholder="Не переводить (использовать оригинал)"
                            allowClear showSearch optionFilterProp="label"
                            options={LANG_OPTIONS} value={targetLang} onChange={setTargetLang}
                        />
                    </div>
                </Space>
            );
        }

        if (step === 'edit') {
            return (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {error && <Alert type="error" message={error} showIcon />}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        <Title level={5} style={{ margin: 0 }}>
                            <FileTextOutlined /> {template?.name ?? 'Набор триггеров'} —{' '}
                            <Text type="secondary">{template?.triggers.length} триггеров</Text>
                        </Title>
                        <Space size={6} wrap>
                            <Button
                                size="small"
                                icon={<SaveOutlined />}
                                onClick={openSavePresetModal}
                            >
                                Сохранить как пресет
                            </Button>
                            {inlinePushCount > 0 && (
                                <Tag color="orange" icon={<ThunderboltOutlined />}>
                                    {inlinePushCount} авто-шаблонов
                                </Tag>
                            )}
                            {targetLang && (
                                <Tag color="blue" icon={<TranslationOutlined />}>
                                    → {SUPPORTED_LANGUAGES.find(l => l.code === targetLang)?.abbr ?? targetLang}
                                </Tag>
                            )}
                        </Space>
                    </div>

                    {segments.length > 0 && (
                        <div style={{ background: token.colorFillQuaternary, borderRadius: 8, padding: '12px 16px', border: `1px solid ${token.colorBorderSecondary}` }}>
                            <Row align="middle" gutter={12}>
                                <Col><TeamOutlined style={{ color: '#666' }} /></Col>
                                <Col flex={1}>
                                    <Text strong style={{ fontSize: 13 }}>Сегмент по умолчанию</Text>
                                    <div style={{ fontSize: 12, color: '#888' }}>Для триггеров без индивидуального сегмента</div>
                                </Col>
                                <Col>
                                    <Select value={globalSegmentId} onChange={setGlobalSegmentId}
                                        style={{ width: 220 }} placeholder="Все пользователи"
                                        allowClear showSearch optionFilterProp="label"
                                        options={segments.map(s => ({ value: s.id, label: s.name }))}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}

                    <Divider style={{ margin: '4px 0' }} />
                    <Space direction="vertical" style={{ width: '100%' }} size={8}>
                        {template?.triggers.map((item, i) => {
                            const merged = getMergedItem(item, i);
                            const cfg = TRIGGER_TYPE_CONFIG[item.trigger_type];
                            return (
                                <div key={i} style={{
                                    border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 8,
                                    padding: '10px 14px', background: token.colorBgContainer,
                                }}>
                                    <Row align="middle" gutter={12}>
                                        <Col flex={1}>
                                            <Space size={8}>
                                                <Tag color={cfg.color} icon={cfg.icon} style={{ margin: 0 }}>{cfg.label}</Tag>
                                                <Text strong style={{ fontSize: 13 }}>{item.name}</Text>
                                                {getTemplateBadge(item, i)}
                                            </Space>
                                            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                                <ClockCircleOutlined style={{ marginRight: 4, color: '#999' }} />
                                                {formatDelay(merged.delay_minutes ?? 0)}
                                                {' · '}
                                                Макс: {merged.max_sends_per_user ?? 1} раз
                                                {' · '}
                                                Cooldown: {merged.cooldown_hours ?? 24}ч
                                                {' · '}
                                                <TeamOutlined style={{ marginRight: 4, color: '#999' }} />
                                                {getSegmentName(merged.segment_id)}
                                            </div>
                                            {item.push && (
                                                <div style={{ fontSize: 11, color: '#999', marginTop: 3 }}>
                                                    📝 {item.push.title} — {item.push.body.slice(0, 80)}
                                                </div>
                                            )}
                                        </Col>
                                        <Col>
                                            <Badge
                                                status={merged.is_active ? 'success' : 'default'}
                                                text={
                                                    <Text style={{ fontSize: 12, cursor: 'pointer' }}
                                                        onClick={() => setOverride(i, { is_active: !merged.is_active })}>
                                                        {merged.is_active ? 'Активно' : 'Нет'}
                                                    </Text>
                                                }
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            );
                        })}
                    </Space>
                </Space>
            );
        }

        if (step === 'processing') {
            return (
                <Space direction="vertical" style={{ width: '100%', padding: '16px 0' }} size="middle">
                    <div style={{ textAlign: 'center' }}>
                        <ThunderboltOutlined style={{ fontSize: 32, color: '#1677ff', marginBottom: 12 }} />
                        <Title level={4} style={{ margin: 0 }}>Обработка...</Title>
                        <Text type="secondary">{progressLabel}</Text>
                    </div>
                    <Progress percent={progress} status="active" />

                    {processingLog.length > 0 && (
                        <div style={{
                            background: '#0d1117', borderRadius: 8, padding: '10px 12px',
                            maxHeight: 200, overflowY: 'auto',
                            fontFamily: 'Consolas, monospace', fontSize: 12, lineHeight: 1.6,
                        }}>
                            {processingLog.map((msg, i) => (
                                <div key={i} style={{
                                    color: msg.startsWith('✅') ? '#56d364'
                                        : msg.startsWith('⚡') ? '#e3b341'
                                        : msg.startsWith('❌') ? '#f85149'
                                        : '#58a6ff',
                                    whiteSpace: 'pre-wrap',
                                }}>
                                    {msg}
                                </div>
                            ))}
                        </div>
                    )}
                </Space>
            );
        }
        return (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {error && <Alert type="error" message={error} showIcon closable onClose={() => setError(null)} />}

                <Alert
                    type="info" showIcon
                    message={`${template?.triggers.length} триггеров готовы к импорту`}
                    description={
                        <Space direction="vertical" size={2}>
                            {targetLang && (
                                <span>Переведено на {SUPPORTED_LANGUAGES.find(l => l.code === targetLang)?.name ?? targetLang}</span>
                            )}
                            {Object.keys(resolvedTemplateIds).length > 0 && (
                                <span>
                                    Шаблоны: {Object.keys(resolvedTemplateIds).length} авто-созданы/найдены
                                </span>
                            )}
                            <span>Проверьте данные перед применением</span>
                        </Space>
                    }
                />

                {processingLog.length > 0 && (
                    <details style={{ fontSize: 12 }}>
                        <summary style={{ cursor: 'pointer', color: '#888', marginBottom: 6 }}>
                            Лог обработки ({processingLog.length} записей)
                        </summary>
                        <div style={{
                            background: '#0d1117', borderRadius: 6, padding: '8px 10px',
                            maxHeight: 140, overflowY: 'auto',
                            fontFamily: 'Consolas, monospace', fontSize: 11, lineHeight: 1.5,
                        }}>
                            {processingLog.map((msg, i) => (
                                <div key={i} style={{
                                    color: msg.startsWith('✅') ? '#56d364'
                                        : msg.startsWith('⚡') ? '#e3b341'
                                        : msg.startsWith('❌') ? '#f85149'
                                        : '#8888aa',
                                }}>
                                    {msg}
                                </div>
                            ))}
                        </div>
                    </details>
                )}

                <Table
                    dataSource={template?.triggers.map((item, i) => getMergedItem(item, i)) ?? []}
                    columns={previewColumns}
                    pagination={false} size="small"
                    rowKey={(_, i) => String(i)}
                    scroll={{ x: 900 }}
                />
            </Space>
        );
    };

    const stepTitles: Record<Step, string> = {
        upload: 'Импорт триггеров из шаблона',
        edit: 'Настройка параметров',
        processing: 'Обработка',
        preview: 'Предпросмотр триггеров',
    };

    return (
        <>
            <Tooltip title={disabled ? 'Сначала выберите приложение' : 'Загрузить триггеры из JSON'}>
                <Button icon={<ThunderboltOutlined />} disabled={disabled} onClick={handleOpen}>
                    Загрузить триггеры из шаблона
                </Button>
            </Tooltip>

            <Modal
                title={stepTitles[step]}
                open={open} onCancel={handleClose}
                footer={footer()}
                width={step === 'preview' ? 1050 : 700}
                destroyOnClose
            >
                {content()}
            </Modal>
            <Modal
                title="Сохранить как пресет"
                open={savePresetOpen}
                onCancel={() => setSavePresetOpen(false)}
                onOk={handleSavePreset}
                okText="Сохранить"
                cancelText="Отмена"
                confirmLoading={savingPreset}
                okButtonProps={{ disabled: !savePresetName.trim() }}
                destroyOnClose
            >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Alert
                        type="info"
                        showIcon
                        message="Пресет будет доступен всем пользователям"
                        description="Сохраняются текущие параметры (триггеры, сегмент по умолчанию)."
                    />
                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 4 }}>Название</Text>
                        <Input
                            placeholder="Например: Onboarding Triggers EN"
                            value={savePresetName}
                            onChange={(e) => setSavePresetName(e.target.value)}
                            maxLength={255}
                        />
                    </div>
                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 4 }}>Описание (необязательно)</Text>
                        <Input.TextArea
                            placeholder="Кратко: для каких приложений / какой набор триггеров"
                            value={savePresetDesc}
                            onChange={(e) => setSavePresetDesc(e.target.value)}
                            rows={3}
                            maxLength={500}
                        />
                    </div>
                </Space>
            </Modal>
        </>
    );
};

export default EventTriggerLoader;